import clsx from 'clsx'
import { type ReactNode, useRef } from 'react'
import { useFocusTrap } from '@/components/ui/focus-trap.ts'
import { Overlay } from '@/components/ui/Overlay.tsx'

/**
 * The dialog every plane uses: a scrim, one panel, a title with its icon, and a footer.
 *
 * Extracted from the people plane's three dialogs when the groups plane needed six more — two
 * copies of "what a dialog looks like" is how they start to disagree. What it guarantees is the
 * part that is easy to get wrong: focus moves in, Tab wraps, Escape closes, focus returns to what
 * opened it (`useFocusTrap`), and the panel is labelled by its title.
 *
 * Escape inside a combobox is the combobox's (it closes its popup and stops the event), so a
 * picker inside a dialog can close its list without closing the dialog.
 */
export const Dialog = ({
  titleId,
  icon,
  title,
  lead,
  onClose,
  width = 'md',
  children
}: {
  titleId: string
  icon: ReactNode
  title: string
  lead?: ReactNode
  onClose: () => void
  width?: 'md' | 'lg'
  children: ReactNode
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  useFocusTrap(panelRef, { onEscape: onClose })

  return (
    <Overlay>
      <div className="fade-in fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[var(--scrim)]/45 p-4 backdrop-blur-[2px] sm:p-8">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal
          aria-labelledby={titleId}
          tabIndex={-1}
          className={clsx(
            'w-full overflow-clip rounded-shell border border-rule bg-card-solid shadow-frame focus:outline-none',
            width === 'md' ? 'max-w-lg' : 'max-w-2xl'
          )}
        >
          <div className="flex items-start gap-3 px-5 pt-5">
            <span
              aria-hidden
              className="mt-0.5 flex h-[30px] w-[30px] flex-none items-center justify-center rounded-tile border border-rule bg-card text-fg-muted"
            >
              {icon}
            </span>
            <div className="min-w-0">
              <h2 id={titleId} className="text-[19px] text-pretty">
                {title}
              </h2>
              {lead && <div className="mt-1 text-pretty text-[13px] leading-[1.55] text-fg-muted">{lead}</div>}
            </div>
          </div>
          {children}
        </div>
      </div>
    </Overlay>
  )
}

export const DialogBody = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={clsx('space-y-4 px-5 pt-4', className)}>{children}</div>
)

export const DialogFooter = ({ children }: { children: ReactNode }) => (
  <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-rule bg-card px-5 py-3.5">
    {children}
  </div>
)
